import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TablePagination } from '@/components/ui/table-pagination'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DeleteButton, EditButton } from '@/components/ui/action-buttons'
import {
  PageContainer,
  PageCard,
  PageHeader,
  PageHeaderContent,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  PageContent
} from '@/components/ui/page'
import { Loader2, Plus, AlertTriangle } from 'lucide-react'
import { CopyButton } from '@/components/widget-keys/CopyButton'
import { ColorPickerField } from '@/components/widget-keys/ColorPickerField'
import { LogoUpload } from '@/components/widget-keys/LogoUpload'
import { contrastText } from '@/widget/branding'

// Hooks
import { useBookingKeys, useBookingKey, type BookingKey } from '@/hooks/useBookingKeys'
import { env } from '@/lib/env'
import { useBookingKeyMutations } from '@/hooks/useBookingKeyMutations'
import { useCalendars } from '@/hooks/useCalendars'
import { useEventTypes } from '@/hooks/useEventTypes'

// Types
import type { BookingKeyCreateDTO, BookingKeyUpdateDTO, BookingKeyResponseDTO } from '@/api/generated/models'

export const Route = createFileRoute('/booking-keys')({
  component: BookingKeys,
  errorComponent: RouteErrorFallback,
})

const BOOKING_DEFAULT_PRIMARY_COLOR = '#18181b'
const BOOKING_DEFAULT_BACKGROUND_COLOR = '#ffffff'

const bookingPageUrl = (key: string) => `${env.APP_ORIGIN}/booking/${key}`

interface LinkSelectItem {
  id: string
  name: string
  hint?: string
}

function LinkSelectList({
  idPrefix,
  items,
  value,
  onChange,
  emptyText,
}: {
  idPrefix: string
  items: LinkSelectItem[]
  value: string[]
  onChange: (value: string[]) => void
  emptyText: string
}) {
  if (items.length === 0) {
    return (
      <div className="rounded-md border p-3 text-sm text-muted-foreground">{emptyText}</div>
    )
  }
  return (
    <div className="grid max-h-48 gap-2 overflow-y-auto rounded-md border p-3">
      {items.map((item) => (
        <div key={item.id} className="flex items-start space-x-2">
          <Checkbox
            id={`${idPrefix}-${item.id}`}
            checked={value.includes(item.id)}
            onCheckedChange={(checked) =>
              onChange(
                checked === true
                  ? [...value, item.id]
                  : value.filter((v) => v !== item.id)
              )
            }
            className="mt-0.5"
          />
          <Label htmlFor={`${idPrefix}-${item.id}`} className="font-normal leading-tight">
            <span>{item.name}</span>
            {item.hint && <span className="text-muted-foreground"> — {item.hint}</span>}
          </Label>
        </div>
      ))}
    </div>
  )
}

function BookingKeys() {
  // State
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view')
  const [detailTab, setDetailTab] = useState<'settings' | 'design'>('settings')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Create dialog state
  const [createStep, setCreateStep] = useState<'form' | 'success'>('form')
  const [createLabel, setCreateLabel] = useState('')
  const [createCalendarIds, setCreateCalendarIds] = useState<string[]>([])
  const [createEventTypeIds, setCreateEventTypeIds] = useState<string[]>([])
  const [createdKey, setCreatedKey] = useState<BookingKeyResponseDTO | null>(null)

  // Edit dialog state
  const [editLabel, setEditLabel] = useState('')
  const [editCalendarIds, setEditCalendarIds] = useState<string[]>([])
  const [editEventTypeIds, setEditEventTypeIds] = useState<string[]>([])
  const [editActive, setEditActive] = useState(true)
  const [editSendConfirmationEmail, setEditSendConfirmationEmail] = useState(false)

  // Design tab state
  const [primaryColor, setPrimaryColor] = useState<string | null>(null)
  const [backgroundColor, setBackgroundColor] = useState<string | null>(null)
  const [textColor, setTextColor] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoRemoveRequested, setLogoRemoveRequested] = useState(false)

  const offset = (currentPage - 1) * pageSize

  // Queries
  const { data: bookingKeys = [], isLoading, error } = useBookingKeys(pageSize, offset)
  const { data: calendars = [] } = useCalendars()
  const { data: eventTypes = [] } = useEventTypes()
  const { data: selectedKey } = useBookingKey(selectedId)

  // Mutations
  const { createBookingKey, updateBookingKey, deleteBookingKey, uploadBookingKeyLogo, deleteBookingKeyLogo } = useBookingKeyMutations()

  const calendarName = (id: string) => calendars.find((c) => c.id === id)?.name ?? id
  const eventTypeName = (id: string) => eventTypes.find((t) => t.id === id)?.name ?? id

  const calendarItems: LinkSelectItem[] = calendars.map((c) => ({ id: c.id, name: c.name }))
  const eventTypeItems: LinkSelectItem[] = eventTypes.map((t) => ({
    id: t.id,
    name: t.name,
    hint: t.booking_available ? undefined : 'nicht öffentlich buchbar',
  }))

  // logo_url is backend-relative; resolve against the API host, not the dashboard origin
  const selectedLogoUrl = selectedKey?.logo_url
    ? new URL(selectedKey.logo_url, env.API_BASE_URL).toString()
    : null

  const designSavingRef = useRef(false)

  // Populate edit form when full key is loaded
  useEffect(() => {
    if (selectedKey && !designSavingRef.current) {
      setEditLabel(selectedKey.label)
      setEditCalendarIds(selectedKey.calendar_ids ?? [])
      setEditEventTypeIds(selectedKey.event_type_ids ?? [])
      setEditActive(selectedKey.is_active)
      setEditSendConfirmationEmail(selectedKey.send_confirmation_email)
      setPrimaryColor(selectedKey.primary_color ?? null)
      setBackgroundColor(selectedKey.background_color ?? null)
      setTextColor(selectedKey.text_color ?? null)
      setLogoFile(null)
      setLogoRemoveRequested(false)
    }
  }, [selectedKey])

  // No total-count endpoint: approximate so "Nächste" stays enabled while pages come back full
  const totalItems = bookingKeys.length
  const displayTotalItems =
    totalItems < pageSize
      ? offset + totalItems
      : currentPage * pageSize + (totalItems === pageSize ? pageSize : 0)
  const totalPages = Math.max(1, Math.ceil(displayTotalItems / pageSize))

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  const resetCreate = () => {
    setCreateStep('form')
    setCreateLabel('')
    setCreateCalendarIds([])
    setCreateEventTypeIds([])
    setCreatedKey(null)
  }

  const openCreate = () => {
    resetCreate()
    setCreateDialogOpen(true)
  }

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const createData: BookingKeyCreateDTO = {
      label: createLabel,
      calendar_ids: createCalendarIds,
      event_type_ids: createEventTypeIds
    }

    try {
      const created = await createBookingKey.mutateAsync(createData)
      setCreatedKey(created)
      setCreateStep('success')
    } catch (err) {
      console.error('Failed to create booking key:', err)
    }
  }

  const handleRowClick = (bookingKey: BookingKey) => {
    setSelectedId(bookingKey.id)
    setEditLabel(bookingKey.label)
    setEditCalendarIds(bookingKey.calendar_ids ?? [])
    setEditEventTypeIds(bookingKey.event_type_ids ?? [])
    setEditActive(bookingKey.is_active)
    setEditSendConfirmationEmail(bookingKey.send_confirmation_email)
    setDialogMode('view')
    setDetailTab('settings')
    setDetailDialogOpen(true)
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedId) return

    const updateData: BookingKeyUpdateDTO = {
      label: editLabel,
      calendar_ids: editCalendarIds,
      event_type_ids: editEventTypeIds,
      is_active: editActive,
      send_confirmation_email: editSendConfirmationEmail
    }

    try {
      await updateBookingKey.mutateAsync({ id: selectedId, data: updateData })
      setDialogMode('view')
    } catch (err) {
      console.error('Failed to update booking key:', err)
    }
  }

  const handleReactivate = async () => {
    if (!selectedId) return
    try {
      await updateBookingKey.mutateAsync({ id: selectedId, data: { is_active: true } })
      toast.success('Buchungsseite reaktiviert')
    } catch (err) {
      console.error('Failed to reactivate booking key:', err)
    }
  }

  const handleDelete = async () => {
    if (!selectedId) return
    try {
      await deleteBookingKey.mutateAsync(selectedId)
      setDetailDialogOpen(false)
      setSelectedId(null)
    } catch (err) {
      console.error('Failed to revoke booking key:', err)
    }
  }

  const designDirty =
    !!selectedKey &&
    (primaryColor !== (selectedKey.primary_color ?? null) ||
      backgroundColor !== (selectedKey.background_color ?? null) ||
      textColor !== (selectedKey.text_color ?? null) ||
      logoFile !== null ||
      logoRemoveRequested)

  const resetDesignState = () => {
    if (!selectedKey) return
    setPrimaryColor(selectedKey.primary_color ?? null)
    setBackgroundColor(selectedKey.background_color ?? null)
    setTextColor(selectedKey.text_color ?? null)
    setLogoFile(null)
    setLogoRemoveRequested(false)
  }

  const handleResetDesignToDefault = () => {
    setPrimaryColor(null)
    setBackgroundColor(null)
    setTextColor(null)
    setLogoFile(null)
    if (selectedKey?.logo_url) {
      setLogoRemoveRequested(true)
    }
  }

  const isDesignSaving = uploadBookingKeyLogo.isPending || deleteBookingKeyLogo.isPending || updateBookingKey.isPending

  const handleDesignSave = async () => {
    if (!selectedId) return
    designSavingRef.current = true
    try {
      if (logoFile) {
        await uploadBookingKeyLogo.mutateAsync({ id: selectedId, file: logoFile })
      } else if (logoRemoveRequested) {
        await deleteBookingKeyLogo.mutateAsync(selectedId)
      }
    } catch (err) {
      designSavingRef.current = false
      console.error('Failed to update booking key logo:', err)
      return
    }

    try {
      await updateBookingKey.mutateAsync({
        id: selectedId,
        data: {
          primary_color: primaryColor,
          background_color: backgroundColor,
          text_color: textColor
        }
      })
      setLogoFile(null)
      setLogoRemoveRequested(false)
      toast.success('Design gespeichert')
    } catch (err) {
      console.error('Failed to update booking key design:', err)
    } finally {
      designSavingRef.current = false
    }
  }

  const isInert = (bookingKey: Pick<BookingKey, 'calendar_ids' | 'event_type_ids'>) =>
    (bookingKey.calendar_ids ?? []).length === 0 || (bookingKey.event_type_ids ?? []).length === 0

  const statusBadge = (bookingKey: Pick<BookingKey, 'is_active' | 'calendar_ids' | 'event_type_ids'>) => {
    if (!bookingKey.is_active) {
      return <Badge variant="secondary">Widerrufen</Badge>
    }
    if (isInert(bookingKey)) {
      return (
        <Badge variant="outline" className="bg-amber-100 text-amber-800 hover:bg-amber-100">
          Inert
        </Badge>
      )
    }
    return <Badge variant="default">Aktiv</Badge>
  }

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <PageHeaderHeading>Online-Buchung</PageHeaderHeading>
              <PageHeaderDescription>
                Erstellen Sie Buchungsseiten für die öffentliche Online-Terminbuchung.
              </PageHeaderDescription>
            </PageHeaderTitle>
            <PageHeaderActions>
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Neue Buchungsseite
              </Button>
            </PageHeaderActions>
          </PageHeaderContent>
        </PageHeader>
        <PageContent className="p-6 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-32 text-red-600">
              <span>Fehler beim Laden der Buchungsseiten.</span>
            </div>
          )}

          {!isLoading && !error && bookingKeys.length === 0 && (
            <div className="flex items-center justify-center h-32 text-gray-500">
              <span>Noch keine Buchungsseiten vorhanden.</span>
            </div>
          )}

          {!isLoading && !error && bookingKeys.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Kalender</TableHead>
                  <TableHead>Terminarten</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookingKeys.map((bookingKey) => (
                  <TableRow
                    key={bookingKey.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(bookingKey)}
                  >
                    <TableCell className="font-medium">{bookingKey.label}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-sm">{bookingKey.key.slice(0, 10)}…</span>
                        <CopyButton
                          value={bookingKey.key}
                          label="Key kopieren"
                          variant="ghost"
                          className="h-6 w-6"
                          iconClassName="h-3.5 w-3.5"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      {(bookingKey.calendar_ids ?? []).length === 0 ? (
                        <span className="text-amber-600">⚠ 0</span>
                      ) : (
                        <span>{(bookingKey.calendar_ids ?? []).length}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(bookingKey.event_type_ids ?? []).length === 0 ? (
                        <span className="text-amber-600">⚠ 0</span>
                      ) : (
                        <span>{(bookingKey.event_type_ids ?? []).length}</span>
                      )}
                    </TableCell>
                    <TableCell>{statusBadge(bookingKey)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </PageContent>

        {!isLoading && !error && bookingKeys.length > 0 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={displayTotalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </PageCard>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {createStep === 'form' ? (
            <>
              <DialogHeader>
                <DialogTitle>Neue Buchungsseite</DialogTitle>
                <DialogDescription>
                  Erstellen Sie eine Buchungsseite für die öffentliche Online-Terminbuchung.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleCreateSubmit}>
                <div className="grid gap-4 mt-2">
                  <div className="grid gap-2">
                    <Label htmlFor="create-label">Name</Label>
                    <Input
                      id="create-label"
                      value={createLabel}
                      onChange={(e) => setCreateLabel(e.target.value)}
                      placeholder="z.B. Website Terminbuchung"
                      required
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>Kalender</Label>
                    <LinkSelectList
                      idPrefix="create-calendar"
                      items={calendarItems}
                      value={createCalendarIds}
                      onChange={setCreateCalendarIds}
                      emptyText="Keine Kalender vorhanden."
                    />
                    <p className="text-sm text-muted-foreground">
                      Nur in den ausgewählten Kalendern können Termine gebucht werden.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label>Terminarten</Label>
                    <LinkSelectList
                      idPrefix="create-event-type"
                      items={eventTypeItems}
                      value={createEventTypeIds}
                      onChange={setCreateEventTypeIds}
                      emptyText="Keine Terminarten vorhanden."
                    />
                    <p className="text-sm text-muted-foreground">
                      Terminarten ohne Online-Buchung erscheinen auf der Buchungsseite nicht.
                    </p>
                  </div>
                </div>

                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={createBookingKey.isPending}>
                    {createBookingKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Erstellen
                  </Button>
                </DialogFooter>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>✓ „{createdKey?.label}" wurde erstellt.</DialogTitle>
                <DialogDescription>
                  Der Key ist jederzeit in den Details abrufbar.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 mt-2">
                <div className="grid gap-2">
                  <Label>Key</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                      {createdKey?.key}
                    </code>
                    <CopyButton value={createdKey?.key ?? ''} label="Key kopieren" />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Link zur Buchungsseite</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                      {bookingPageUrl(createdKey?.key ?? '')}
                    </code>
                    <CopyButton value={bookingPageUrl(createdKey?.key ?? '')} label="Link kopieren" />
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setCreateDialogOpen(false)
                    resetCreate()
                  }}
                >
                  Fertig
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail/Edit Dialog */}
      <Dialog
        open={detailDialogOpen}
        onOpenChange={(open) => {
          setDetailDialogOpen(open)
          if (!open) resetDesignState()
        }}
      >
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'view' ? 'Buchungsseiten-Details' : 'Buchungsseite bearbeiten'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'view' ? 'Details der Buchungsseite anzeigen' : 'Einstellungen der Buchungsseite bearbeiten'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={detailTab} onValueChange={(v) => setDetailTab(v as 'settings' | 'design')}>
            <TabsList>
              <TabsTrigger value="settings">Einstellungen</TabsTrigger>
              <TabsTrigger value="design">Design</TabsTrigger>
            </TabsList>

            <TabsContent value="settings">
          {dialogMode === 'view' ? (
            <>
              <div className="grid gap-4 mt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Name</Label>
                    <p className="font-medium">{selectedKey?.label}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <div className="mt-1">
                      {selectedKey && statusBadge(selectedKey)}
                    </div>
                  </div>
                </div>

                {selectedKey && (selectedKey.calendar_ids ?? []).length > 0 && (
                  <div>
                    <Label className="text-muted-foreground">Kalender</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(selectedKey.calendar_ids ?? []).map((id) => (
                        <Badge key={id} variant="outline">{calendarName(id)}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedKey && (selectedKey.event_type_ids ?? []).length > 0 && (
                  <div>
                    <Label className="text-muted-foreground">Terminarten</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(selectedKey.event_type_ids ?? []).map((id) => (
                        <Badge key={id} variant="outline">{eventTypeName(id)}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedKey && (
                  <div>
                    <Label className="text-muted-foreground">Buchungsbestätigung per E-Mail</Label>
                    <p className="font-medium">{selectedKey.send_confirmation_email ? 'An' : 'Aus'}</p>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label className="text-muted-foreground">Key</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                      {selectedKey?.key}
                    </code>
                    <CopyButton value={selectedKey?.key ?? ''} label="Key kopieren" />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label className="text-muted-foreground">Link zur Buchungsseite</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                      {bookingPageUrl(selectedKey?.key ?? '')}
                    </code>
                    <CopyButton value={bookingPageUrl(selectedKey?.key ?? '')} label="Link kopieren" />
                  </div>
                </div>

                {selectedKey && (selectedKey.calendar_ids ?? []).length === 0 && (
                  <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-amber-800">
                      Für diese Buchungsseite sind keine Kalender verknüpft — es können keine Termine gebucht werden. Verknüpfen Sie mindestens einen Kalender.
                    </AlertDescription>
                  </Alert>
                )}

                {selectedKey && (selectedKey.event_type_ids ?? []).length === 0 && (
                  <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-amber-800">
                      Für diese Buchungsseite sind keine Terminarten verknüpft — es können keine Termine gebucht werden. Verknüpfen Sie mindestens eine Terminart.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <DialogFooter className="mt-4">
                {selectedKey?.is_active ? (
                  <>
                    <EditButton onClick={() => setDialogMode('edit')} />
                    <DeleteButton
                      onDelete={handleDelete}
                      itemName={selectedKey?.label || ''}
                      confirmationTitle="Buchungsseite widerrufen?"
                      confirmationDescription="Über diese Buchungsseite können danach keine Termine mehr gebucht werden. Sie können sie später reaktivieren."
                    />
                  </>
                ) : (
                  <div className="flex items-center justify-between w-full gap-4">
                    <span className="text-sm text-muted-foreground">Diese Buchungsseite ist widerrufen.</span>
                    <div className="flex gap-2">
                      <EditButton onClick={() => setDialogMode('edit')} />
                      <Button type="button" onClick={handleReactivate} disabled={updateBookingKey.isPending}>
                        {updateBookingKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Reaktivieren
                      </Button>
                    </div>
                  </div>
                )}
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleEditSubmit}>
              <div className="grid gap-4 mt-2">
                <div className="grid gap-2">
                  <Label htmlFor="edit-label">Name</Label>
                  <Input
                    id="edit-label"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Kalender</Label>
                  <LinkSelectList
                    idPrefix="edit-calendar"
                    items={calendarItems}
                    value={editCalendarIds}
                    onChange={setEditCalendarIds}
                    emptyText="Keine Kalender vorhanden."
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Terminarten</Label>
                  <LinkSelectList
                    idPrefix="edit-event-type"
                    items={eventTypeItems}
                    value={editEventTypeIds}
                    onChange={setEditEventTypeIds}
                    emptyText="Keine Terminarten vorhanden."
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="edit-active"
                    checked={editActive}
                    onCheckedChange={setEditActive}
                  />
                  <Label htmlFor="edit-active">Aktiv</Label>
                </div>

                <div className="grid gap-2">
                  <h3 className="text-sm font-medium">Benachrichtigungen</h3>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="edit-send-confirmation-email"
                      checked={editSendConfirmationEmail}
                      onCheckedChange={setEditSendConfirmationEmail}
                    />
                    <Label htmlFor="edit-send-confirmation-email">Buchungsbestätigung per E-Mail senden</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">Kundinnen und Kunden erhalten nach der Buchung eine E-Mail mit den Termindaten. Die E-Mail nutzt Farben und Logo aus dem Tab „Design“.</p>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setDialogMode('view')}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={updateBookingKey.isPending}>
                  {updateBookingKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </DialogFooter>
            </form>
          )}
            </TabsContent>

            <TabsContent value="design">
              <div className="grid gap-4 mt-2">
                <ColorPickerField
                  label="Primärfarbe"
                  value={primaryColor}
                  onChange={setPrimaryColor}
                  defaultColor={BOOKING_DEFAULT_PRIMARY_COLOR}
                />
                <ColorPickerField
                  label="Hintergrund"
                  value={backgroundColor}
                  onChange={setBackgroundColor}
                  defaultColor={BOOKING_DEFAULT_BACKGROUND_COLOR}
                />
                <ColorPickerField
                  label="Schrift"
                  value={textColor}
                  onChange={setTextColor}
                  defaultColor={contrastText(backgroundColor ?? BOOKING_DEFAULT_BACKGROUND_COLOR)}
                  placeholder="Auto"
                />

                <div className="grid gap-2">
                  <Label>Logo</Label>
                  <LogoUpload
                    currentLogoUrl={selectedLogoUrl}
                    pendingFile={logoFile}
                    removeRequested={logoRemoveRequested}
                    onFileSelected={(file) => {
                      setLogoFile(file)
                      setLogoRemoveRequested(false)
                    }}
                    onRemove={() => {
                      setLogoFile(null)
                      setLogoRemoveRequested(true)
                    }}
                  />
                </div>

                <div>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-muted-foreground"
                    onClick={handleResetDesignToDefault}
                  >
                    Alles auf Standard zurücksetzen
                  </Button>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetDesignState()
                    setDetailDialogOpen(false)
                  }}
                >
                  Abbrechen
                </Button>
                <Button type="button" onClick={handleDesignSave} disabled={!designDirty || isDesignSaving}>
                  {isDesignSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
